import { beginOperation, withOperation } from '../ui/operation-progress.js';
/**
 * Import/Export Manager
 * Handles project import/export functionality.
 * Extracted from main.js for better separation of concerns.
 */

import { copyInterfaceFields } from "../../../engine/sync/map-format.js";
import { getCurrentColors } from "../ui/theme.js";
// Markdown export/import drags TipTap (and, transitively, html2pdf/jszip) —
// loaded on demand so it stays out of the boot bundle. Exports are always
// user-triggered, so the await is invisible.
const loadMdExportImport = () => import("../views/markdown/md-export-import.js");
import { saveMapExport } from "../ui/export-flow.js";
import { t, formatDate } from "../../../shared/i18n/index.js";

// Stored callbacks
let callbacks = null;

/**
 * Setup import/export button handlers.
 *
 * @param {Object} cbs - Callbacks object with dependencies:
 *   - getState: () => AppState
 *   - get: (key) => any
 *   - getAll: () => Node[]
 *   - initialize: (data, options) => void
 *   - renderTree: () => void
 *   - switchView: (viewName) => void
 *   - saveToLocalStorage: () => void
 *   - syncFullTree: (data) => void (optional, from CRDT)
 *   - setMetadata: (key, value) => void (optional, from CRDT)
 *   - getMetadata: (key) => any (optional, from CRDT)
 *   - setDocNotes: (notes) => void (optional, from CRDT)
 *   - getDocNotes: () => Array (optional, from CRDT)
 *   - getTagDefinitions: () => Array (optional, from CRDT)
 *   - setTagDefinitions: (defs) => void (optional, from CRDT)
 *   - createMap: (name, folderId, initialState) => Promise<{docId}> (optional, from VaultStorage)
 *   - openDoc: (docId) => Promise<void> (optional, from DocumentLoader)
 *   - getCurrentFolderId: () => string|null (optional)
 *   - openImportBrowser: (importData) => Promise<void> (optional, for vault export import)
 *   - withAllGanttRows: (fn) => result of fn, run with every Gantt row drawn (optional)
 */
export function setupImportExport(cbs) {
  callbacks = cbs;

  const exportBtn = document.getElementById("btn-export");
  const exportDropdown = document.getElementById("export-dropdown");
  const exportSvgOption = document.getElementById("export-svg-option");
  const exportMdOption = document.getElementById("export-md-option");
  const exportPdfOption = document.getElementById("export-pdf-option");
  const exportHtmlOption = document.getElementById("export-html-option");
  const exportZipOption = document.getElementById("export-zip-option");
  const exportLatexOption = document.getElementById("export-latex-option");
  const importBtn = document.getElementById("btn-import");
  const importFile = document.getElementById("import-file");

  if (!exportBtn || !exportDropdown) return;

  function updateExportOptions() {
    const currentView = callbacks.get("currentView");
    const isMd = currentView === "markdown";
    // SVG available for mindmap and gantt
    if (currentView === "mindmap" || currentView === "gantt") {
      exportSvgOption?.classList.remove("disabled");
    } else {
      exportSvgOption?.classList.add("disabled");
    }
    // Markdown-view exports
    exportMdOption?.classList.toggle("disabled", !isMd);
    exportPdfOption?.classList.toggle("disabled", !isMd);
    exportHtmlOption?.classList.toggle("disabled", !isMd);
    exportZipOption?.classList.toggle("disabled", !isMd);
    exportLatexOption?.classList.toggle("disabled", !isMd);
  }

  exportBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    updateExportOptions();
    exportDropdown.classList.toggle("active");
    document.getElementById("import-dropdown")?.classList.remove("active");
    document.getElementById("theme-dropdown")?.classList.remove("active");
  });

  exportDropdown.querySelectorAll(".export-option").forEach((opt) => {
    opt.addEventListener("click", async (e) => {
      if (opt.classList.contains("disabled")) return;
      const format = opt.dataset.format;
      exportDropdown.classList.remove("active");

      try {
        if (format === "json") {
          await exportProject();
        } else if (format === "svg") {
          await exportSVG();
        } else if (format === "csv") {
          await exportCSV();
        } else if (format === "md") {
          await exportMarkdown();
        } else if (format === "pdf" || format === "html" || format === "zip" || format === "latex") {
          await handleMarkdownViewExport(format);
        }
      } catch (err) {
        console.error("Export error:", err);
        alert(t('vault.export.failedRetry'));
      }
    });
  });

  // Close dropdowns on outside click
  document.addEventListener("click", (e) => {
    if (!e.target.closest(".export-selector")) {
      exportDropdown.classList.remove("active");
      importDropdown?.classList.remove("active");
    }
  });

  // Import dropdown
  const importDropdown = document.getElementById("import-dropdown");
  const importJsonOption = document.getElementById("import-json-option");
  const importMdOption = document.getElementById("import-md-option");
  const importMdFile = document.getElementById("import-md-file");

  if (importBtn && importDropdown) {
    importBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      importDropdown.classList.toggle("active");
      exportDropdown.classList.remove("active");
      document.getElementById("theme-dropdown")?.classList.remove("active");
    });

    importJsonOption?.addEventListener("click", () => {
      importDropdown.classList.remove("active");
      importFile?.click();
    });

    importMdOption?.addEventListener("click", () => {
      importDropdown.classList.remove("active");
      importMdFile?.click();
    });

    const importZipOption = document.getElementById("import-zip-option");
    importZipOption?.addEventListener("click", () => {
      importDropdown.classList.remove("active");
      triggerZipImportGlobal();
    });
  }

  if (importFile) {
    importFile.addEventListener("change", (e) => {
      if (e.target.files.length > 0) {
        importProject(e.target.files[0]);
        e.target.value = "";
      }
    });
  }

  if (importMdFile) {
    importMdFile.addEventListener("change", (e) => {
      if (e.target.files.length > 0) {
        importMarkdown(e.target.files[0]);
        e.target.value = "";
      }
    });
  }
}

/**
 * Clean a node for export by removing internal properties.
 * @param {Object} node - The node to clean
 * @returns {Object} Cleaned node
 */
export function cleanNode(node, _depth = 0) {
  if (_depth > 500) {
    // Prevent stack overflow from maliciously deep trees
    return { id: node.id, name: node.name, color: node.color, children: [] };
  }
  const clean = { ...node };
  // A link carries only its reference. In the view its fields and children
  // are the linked map's content, which belongs to that other map: copying
  // it would put a hidden copy under the link.
  if (clean.linkedDocId) {
    const result = {
      id: clean.id,
      name: clean.linkedDocTitle || clean.name,
      linkedDocId: clean.linkedDocId,
      children: [],
    };
    if (clean.linkedDocTitle) result.linkedDocTitle = clean.linkedDocTitle;
    return result;
  }
  // Keep essential properties
  const result = {
    id: clean.id,
    name: clean.name,
    color: clean.color,
    children: clean.children ? clean.children.map((c) => cleanNode(c, _depth + 1)) : [],
  };
  // Copy optional properties
  // node.status is intentionally NOT exported: post-migration it can drift
  // from tags (we observed nodes where tags say Done but status still says
  // Todo). Re-importing legacy status would re-trigger migrateStatusToTags
  // on the destination doc and cross-pollinate tag IDs from a second
  // mandatory-tag set. Tags are the only source of truth.
  if (clean.tags?.length) result.tags = clean.tags;
  if (clean.createdAt) result.createdAt = clean.createdAt;
  if (clean.startDate) result.startDate = clean.startDate;
  if (clean.endDate) result.endDate = clean.endDate;
  if (clean.estimate) result.estimate = clean.estimate;
  if (clean.notes) result.notes = clean.notes;
  if (clean.collapsed) result.collapsed = clean.collapsed;
  // An interface's own fields ("holon:type") go with the map, as everywhere.
  return copyInterfaceFields(clean, result);
}

/**
 * Export the current project to JSON.
 * @returns {boolean} Success status
 */
export async function exportProject() {
  try {
    const state = callbacks.getState();
    const notesPanelWidth =
      localStorage.getItem("notes-panel-width") || "420px";

    // Get document-level notes from CRDT. In collaborative mode the
    // authoritative content lives in a Y.XmlFragment per note; the
    // array entry's plain `content` string is only refreshed on editor
    // blur, so it can lag mid-edit. Pull fragment content for fresh
    // export when available, falling back to the array string.
    const rawDocNotes = callbacks.getDocNotes?.() || [];
    const collabNotes = !!callbacks.isCollabDocNotesEnabled?.();
    const docNotes = collabNotes && callbacks.serializeDocNote
      ? rawDocNotes.map((note) => {
          try {
            const fragContent = callbacks.serializeDocNote(note.id);
            if (fragContent && fragContent !== note.content) {
              return { ...note, content: fragContent };
            }
          } catch { /* keep original on failure */ }
          return note;
        })
      : rawDocNotes;

    // Tag system: definitions + per-doc kanban column ordering.
    // Tags replaced the legacy `status` field; node.tags references these ids.
    const tagDefinitions = callbacks.getTagDefinitions?.() || [];
    const kanbanColumns = callbacks.getMetadata?.("kanbanColumns") || [];

    // Execution entries (Today lists: which node was on which period's list,
    // ticks, pins) — including removed ones, so the record survives a round trip.
    const execution = callbacks.getExecutionItems?.({ includeRemoved: true }) || [];

    const exportData = {
      version: 1,
      name: state.data.name,
      data: cleanNode(state.data),
      docNotes: docNotes,
      execution: execution,
      routines: callbacks.getRoutineEvents?.() || [],
      taskPostponements: callbacks.getTaskPostponementEvents?.() || [],
      tagDefinitions: tagDefinitions,
      kanbanColumns: kanbanColumns,
      ganttScale: state.ganttScale,
      ganttZoom: state.ganttZoom,
      userSetProjectDates: state.userSetProjectDates,
      notesPanelWidth: notesPanelWidth,
      exportedAt: new Date().toISOString(),
    };

    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const filename = `${state.data.name.replace(/[^a-z0-9]/gi, "_").toLowerCase()}_${new Date().toISOString().split("T")[0]}.json`;
    return !!await saveMapExport(blob, filename, { data: exportData, docId: callbacks.getDocId?.() });
  } catch (e) {
    console.error("Failed to export project:", e);
    alert(t('vault.export.projectFailed'));
    return false;
  }
}

/**
 * Import a project from a JSON file.
 * Detects format (vault export vs single-map JSON) and either opens the
 * import browser or creates a new map in the vault.
 * @param {File} file - The file to import
 */
export async function importProject(file) {
  try {
    const { readExportFile } = await import('../../../engine/crypto/data-export.js');
    const importData = await readExportFile(file);
    if (importData._markdownFile) {
      const { importZip } = await loadMdExportImport();
      await importZip(file, { ...callbacks, onUpdateNode: callbacks.onUpdateNode || callbacks.updateNode }, importData._markdownArchive);
      return;
    }
    // Detect format: vault export vs single-map JSON
    if (importData.version === 2 && Array.isArray(importData.documents)) {
      // Vault export - open import browser (Phase 3)
      if (callbacks.openImportBrowser) {
        await callbacks.openImportBrowser(importData);
      } else {
        alert(t('vault.import.vaultUnsupported'));
      }
      return;
    }

    // Single-map JSON - validate
    if (!importData.data || !importData.data.id) {
      throw new Error(t('vault.import.invalidProject'));
    }

    // Strip legacy `node.status` on any node that already carries `tags`,
    // so migrateStatusToTags doesn't run on the destination doc and add a
    // SECOND mandatory-tag id (the destination's) alongside the source's.
    // Older exports (pre-tags-migration) have status only and no tags —
    // those are left alone so the destination's migration can convert them.
    stripStaleStatusInPlace(importData.data);

    // Ask for map name
    const defaultName = importData.name || importData.data.name || t('vault.import.defaultMapName');
    const mapName = prompt(t('vault.import.mapNamePrompt'), defaultName);
    if (!mapName) return; // User cancelled

    // Create new map if callbacks available, otherwise fall back to overwrite
    if (callbacks.createMap && callbacks.openDoc) {
      await withOperation(t('vault.import.mapTitle'), async progress => {
        progress.update({ label: t('vault.import.restoringMap') });
        await importAsSingleNewMap(importData, mapName, progress);
      });
    } else {
      // Fallback: legacy overwrite behavior (no vault context)
      if (importData._attachmentArchive) throw new Error(t('vault.import.needVault'));
      importOverwrite(importData);
    }
  } catch (err) {
    console.error('Import failed:', err);
    alert(t('vault.import.failed', { error: err.message }));
  }
}

/**
 * Import a single-map JSON as a new map in the vault.
 * Creates the map via VaultStorage and opens it with DocumentLoader.
 * @param {Object} importData - The parsed import JSON
 * @param {string} mapName - The name for the new map
 */
async function importAsSingleNewMap(importData, mapName, progress) {
  try {
    // Build initial state from imported data. docNotes / tagDefinitions
    // / kanbanColumns are bundled into the pending initial state so
    // they're applied inside handleDocOpen's initialization branch —
    // same syncing window as syncFullTree. Writing them after openDoc
    // returned was racing the post-init UI re-renders, which is why
    // imported notes and tags appeared blank until a reload.
    const importedRootId = importData.data.id;
    const docNotesToSync = Array.isArray(importData.docNotes) && importData.docNotes.length > 0
      ? importData.docNotes
      : (Array.isArray(importData.data?.notes) && importData.data.notes.length > 0
          ? importData.data.notes
          : null);
    const initialState = {
      data: importData.data,
      ganttScale: importData.ganttScale || "auto",
      ganttZoom: importData.ganttZoom || 1,
      focusId: importedRootId,
      selectedId: importedRootId,
      view: "dashboard",
      userSetProjectDates: importData.userSetProjectDates || false,
      hideCompleted: false,
      docNotes: docNotesToSync || undefined,
      execution: Array.isArray(importData.execution) ? importData.execution : undefined,
      routines: Array.isArray(importData.routines) ? importData.routines : undefined,
      taskPostponements: Array.isArray(importData.taskPostponements) ? importData.taskPostponements : undefined,
      tagDefinitions: Array.isArray(importData.tagDefinitions) ? importData.tagDefinitions : undefined,
      kanbanColumns: Array.isArray(importData.kanbanColumns) ? importData.kanbanColumns : undefined,
    };
    initialState.data.name = mapName;

    // Restore notes panel width if present
    if (importData.notesPanelWidth) {
      localStorage.setItem("notes-panel-width", importData.notesPanelWidth);
    }

    // Create new map in vault
    const folderId = callbacks.getCurrentFolderId?.() || null;
    const { docId } = await callbacks.createMap(mapName, folderId, initialState);
    if (importData._attachmentArchive) {
      const { restoreMapAttachments, restoredAttachmentNote } = await import('../../../engine/services/map-archive-import.js');
      const { remapAttachmentData } = await import('../../../shared/utils/map-archive.js');
      const { VaultStorage } = await import('../../../engine/crypto/vault-storage.js');
      const archive = importData._attachmentArchive;
      const restored = await restoreMapAttachments(archive, archive.manifest.documentIds[0], docId, importData, { onProgress: progress.update });
      const remapped = remapAttachmentData(initialState, restored.replacements);
      const attachmentNote = restoredAttachmentNote(archive, archive.manifest.documentIds[0], importData, restored.replacements);
      if (attachmentNote) remapped.docNotes = [...(remapped.docNotes || []), attachmentNote];
      VaultStorage.setPendingInitialState(docId, remapped);
      const warnings = [...(archive.manifest.warnings || []), ...restored.warnings];
      if (warnings.length) {
        progress.finish(t('vault.import.finishedWithWarnings'), true);
        (await import('../ui/export-flow.js')).showExportWarnings(warnings);
      }
    }

    // Open the newly created map — handleDocOpen consumes the pending
    // initial state, including docNotes / tagDefinitions / kanbanColumns.
    await callbacks.openDoc(docId);
  } catch (err) {
    console.error("Failed to create map from import:", err);
    throw err;
  }
}

/**
 * Walk the imported tree and remove `status` from any node that already has
 * a non-empty `tags` array. The destination doc will run migrateStatusToTags
 * once its mandatory tag definitions are created (initDefaultTags), and we
 * don't want it cross-pollinating ids onto nodes that already have proper
 * tag references from the source doc.
 *
 * Mutates the tree in place — caller passes the imported `data` subtree.
 */
function stripStaleStatusInPlace(node) {
  if (!node || typeof node !== "object") return;
  if (Array.isArray(node.tags) && node.tags.length > 0 && "status" in node) {
    delete node.status;
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) stripStaleStatusInPlace(child);
  }
}

/**
 * Apply tagDefinitions and kanbanColumns from an imported export to the active doc.
 * Older exports (pre-tags) won't include these, so we leave the doc's defaults alone.
 */
function applyTagMetadataFromImport(importData) {
  if (Array.isArray(importData.tagDefinitions) && callbacks.setTagDefinitions) {
    callbacks.setTagDefinitions(importData.tagDefinitions);
  }
  if (Array.isArray(importData.kanbanColumns) && callbacks.setMetadata) {
    callbacks.setMetadata("kanbanColumns", importData.kanbanColumns);
  }
}

/**
 * Legacy import behavior: overwrite the current document with imported data.
 * Used as fallback when vault context (createMap/openDoc) is not available.
 * @param {Object} importData - The parsed import JSON
 */
function importOverwrite(importData) {
  if (importData.notesPanelWidth) {
    localStorage.setItem("notes-panel-width", importData.notesPanelWidth);
  }

  const currentDocId = callbacks.get("currentDocId");
  const importedRootId = importData.data.id;
  callbacks.initialize(importData.data, {
    focusId: importedRootId,
    selectedId: importedRootId,
    view: callbacks.get("currentView") || "dashboard",
    hideCompleted: false,
    ganttScale: importData.ganttScale || "auto",
    ganttZoom: importData.ganttZoom || 1,
    userSetProjectDates: importData.userSetProjectDates || false,
    notesPanelOpen: false,
    mindmapTransform: null,
    bubbleTransform: null,
    docId: currentDocId,
  });

  callbacks.renderTree();
  callbacks.switchView(callbacks.get("currentView"));
  callbacks.saveToLocalStorage();

  if (callbacks.syncFullTree) {
    callbacks.syncFullTree(importData.data);
    if (importData.userSetProjectDates && callbacks.setMetadata) {
      callbacks.setMetadata("userSetProjectDates", importData.userSetProjectDates);
    }
    const notesToSync = importData.docNotes?.length
      ? importData.docNotes
      : importData.data.notes?.length
        ? importData.data.notes
        : null;
    if (notesToSync && callbacks.setDocNotes) {
      callbacks.setDocNotes(notesToSync);
    }
    applyTagMetadataFromImport(importData);
    if (Array.isArray(importData.routines)) callbacks.setRoutineEvents?.(importData.routines);
    if (Array.isArray(importData.taskPostponements)) callbacks.setTaskPostponementEvents?.(importData.taskPostponements);
    if (Array.isArray(importData.execution)) {
      callbacks.setExecutionItems?.(importData.execution);
    }
  }

  alert(t('vault.import.projectImported'));
}

/**
 * Export SVG based on current view.
 */
export async function exportSVG() {
  const currentView = callbacks.get("currentView");

  if (currentView === "mindmap") {
    return exportMindmapSVG();
  } else if (currentView === "gantt") {
    return exportGanttSVG();
  } else {
    alert(t('vault.export.svgUnavailable'));
  }
}

/**
 * Export the mindmap view as SVG.
 */
export async function exportMindmapSVG() {
  const world = document.querySelector('#m-world');
  const svgEl = world?.querySelector('#m-svg');
  const nodes = [...(world?.querySelectorAll('.mm-node') || [])];
  if (!svgEl || !nodes.length) { alert(t('vault.export.noMindmap')); return; }
  // Nodes are HTML siblings of the SVG connectors, not children of that SVG.
  // Use world coordinates so pan/zoom do not shift the exported geometry.
  const clone = svgEl.cloneNode(true);
  clone.removeAttribute('style');
  clone.removeAttribute('id');
  const ns = 'http://www.w3.org/2000/svg';
  const bounds = [];
  for (const node of nodes) {
    const width = node.offsetWidth, height = node.offsetHeight;
    const x = parseFloat(node.style.left) - width / 2;
    const y = parseFloat(node.style.top) - height / 2;
    bounds.push({ x, y, width, height });
    const foreign = document.createElementNS(ns, 'foreignObject');
    for (const [key, value] of Object.entries({ x, y, width, height })) foreign.setAttribute(key, value);
    const body = document.createElementNS('http://www.w3.org/1999/xhtml', 'div');
    const style = getComputedStyle(node);
    body.style.cssText = `box-sizing:border-box;width:100%;height:100%;display:flex;align-items:center;justify-content:center;text-align:center;white-space:pre-wrap;overflow:hidden;`;
    for (const prop of ['background-color', 'color', 'border', 'border-radius', 'padding', 'font-family', 'font-size', 'font-weight', 'line-height', 'box-shadow']) {
      body.style.setProperty(prop, style.getPropertyValue(prop));
    }
    body.textContent = node.querySelector('.mm-title')?.textContent || '';
    foreign.appendChild(body);
    clone.appendChild(foreign);
  }
  const minX = Math.min(...bounds.map(b => b.x)) - 50;
  const minY = Math.min(...bounds.map(b => b.y)) - 50;
  const width = Math.max(...bounds.map(b => b.x + b.width)) - minX + 50;
  const height = Math.max(...bounds.map(b => b.y + b.height)) - minY + 50;
  clone.setAttribute('viewBox', `${minX} ${minY} ${width} ${height}`);
  clone.setAttribute('width', width);
  clone.setAttribute('height', height);
  const blob = new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml' });
  await saveMapExport(blob, `mindmap_${new Date().toISOString().split('T')[0]}.svg`,
    { data: callbacks.getState().data, docId: callbacks.getDocId?.() });
}

/**
 * Export the Gantt view as SVG.
 */
export async function exportGanttSVG() {
  const ganttBody = document.querySelector("#g-body");
  const ganttHeader = document.querySelector("#g-header");
  const ganttLabels = document.querySelector("#g-labels");

  if (!ganttBody) {
    alert(t('vault.export.noGantt'));
    return;
  }

  // The view draws only the rows near the screen: read them all drawn.
  const withAllRows = callbacks.withAllGanttRows || ((fn) => fn());
  const svgString = withAllRows(() => {
    // Get dimensions
    const headerHeight = ganttHeader?.offsetHeight || 100;
    const labelWidth = ganttLabels?.offsetWidth || 200;
    const bodyWidth = ganttBody.scrollWidth;
    const bodyHeight = ganttBody.scrollHeight;

    const totalWidth = labelWidth + bodyWidth + 20;
    const totalHeight = headerHeight + bodyHeight + 20;

    // Create SVG
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", totalWidth);
    svg.setAttribute("height", totalHeight);
    svg.setAttribute("viewBox", `0 0 ${totalWidth} ${totalHeight}`);
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");

    // Add background
    const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    bg.setAttribute("width", "100%");
    bg.setAttribute("height", "100%");
    bg.setAttribute("fill", "#0d1117");
    svg.appendChild(bg);

    // Add text for labels
    const labels = ganttLabels?.querySelectorAll(".gantt-label-row") || [];
    labels.forEach((label, i) => {
      const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
      text.setAttribute("x", "10");
      text.setAttribute("y", headerHeight + 20 + i * 36 + 20);
      text.setAttribute("fill", "#f0f6fc");
      text.setAttribute("font-size", "12");
      text.setAttribute("font-family", "system-ui, sans-serif");
      text.textContent =
        label.querySelector(".gantt-label-name")?.textContent || "";
      svg.appendChild(text);
    });

    // Add bars
    const bars = ganttBody.querySelectorAll(".gantt-bar");
    bars.forEach((bar, i) => {
      const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      const left = parseFloat(bar.style.left) || 0;
      const width = parseFloat(bar.style.width) || 50;
      const color = bar.style.background || "#3b82f6";

      rect.setAttribute("x", labelWidth + left);
      rect.setAttribute("y", headerHeight + i * 36 + 8);
      rect.setAttribute("width", width);
      rect.setAttribute("height", 24);
      rect.setAttribute("rx", 4);
      rect.setAttribute("fill", color);
      svg.appendChild(rect);
    });

    // Serialize and download
    return new XMLSerializer().serializeToString(svg);
  });
  const blob = new Blob([svgString], { type: "image/svg+xml" });
  await saveMapExport(blob, `gantt_${new Date().toISOString().split("T")[0]}.svg`, { data: callbacks.getState().data, docId: callbacks.getDocId?.() });
}

/**
 * Export data to CSV format.
 */
export async function exportCSV() {
  const state = callbacks.getState();
  // rootId is dynamic, not always "root" (e.g., shared docs)
  const rootId = state.data?.id;
  const allNodes = callbacks.getAll().filter((n) => n.id !== rootId);

  if (allNodes.length === 0) {
    alert(t('vault.export.noData'));
    return;
  }

  // CSV header
  const headers = ["Name", "Status", "Start Date", "End Date", "Notes"];
  const rows = [headers.join(",")];

  // Add rows
  allNodes.forEach((node) => {
    const row = [
      `"${(node.name || "").replace(/"/g, '""')}"`,
      node.status || "todo",
      node.startDate || "",
      node.endDate || "",
      `"${(node.notes || "").replace(/"/g, '""').replace(/\n/g, " ")}"`,
    ];
    rows.push(row.join(","));
  });

  const csvContent = rows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const filename = `${state.data.name.replace(/[^a-z0-9]/gi, "_").toLowerCase()}_${new Date().toISOString().split("T")[0]}.csv`;
  await saveMapExport(blob, filename, { data: state.data, docId: callbacks.getDocId?.() });
}

/**
 * Handle markdown-view-specific exports (PDF, HTML, Zip, LaTeX).
 */
async function handleMarkdownViewExport(format) {
  const state = callbacks.getState();
  const root = state.focusId ? callbacks.find(state.focusId) : state.data;
  if (!root) { alert(t('vault.export.noData')); return; }

  // Document title: use root name, fall back to state.data.name (always the doc title)
  const docTitle = root.name || state.data?.name || t('common.untitled');

  const docId = callbacks.getDocId?.();
  const dek = callbacks.getDek?.();

  // The caller fires this without await, so failures (including the lazy
  // chunk failing to load offline) must surface from here, not bubble up.
  try {
    const md = await loadMdExportImport();
    if (format === "pdf") {
      await md.exportPDF(root, docId, dek, docTitle);
    } else if (format === "html") {
      await md.exportHTML(root, docId, dek, docTitle);
    } else if (format === "zip") {
      await md.exportZip(root, docId, dek, docTitle);
    } else if (format === "latex") {
      await md.exportLaTeX(root, docTitle, { docId });
    }
  } catch (err) {
    console.error("[Export] Markdown export failed:", err);
    alert(t('vault.export.failedConnection'));
  }
}

/**
 * Trigger zip import (MD + images) — creates a new document.
 */
function triggerZipImportGlobal() {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".zip";
  input.style.display = "none";
  document.body.appendChild(input);

  input.addEventListener("change", async () => {
    const file = input.files?.[0];
    input.remove();
    if (!file) return;

    try {
      await importProject(file);
    } catch (err) {
      console.error("[Import] Zip import failed:", err);
      alert(t('vault.import.failed', { error: err.message }));
    }
  });

  input.click();
}

/**
 * Export the current project as a Markdown document.
 * Available only in the markdown view.
 */
export async function exportMarkdown() {
  try {
    const state = callbacks.getState();
    const root = state.focusId ? callbacks.find(state.focusId) : state.data;
    if (!root) {
      alert(t('vault.export.noData'));
      return;
    }

    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    const timeStr = formatDate(now, { hour: "2-digit", minute: "2-digit" });
    const title = root.name || t('common.untitled');

    // Build header
    const headerLines = [`# ${title}`, ""];
    const metaParts = [`**${t('vault.mdExport.date')}** ${dateStr} ${timeStr}`];
    const username = callbacks.getUsername?.();
    if (username) {
      metaParts.push(`**${t('vault.mdExport.author')}** ${username}`);
    }
    headerLines.push(metaParts.join("  \n"));
    headerLines.push("", "---", "");

    // Build body from tree
    const bodyLines = [];

    // Root notes (if any)
    const rootNotes = typeof root.notes === "string" ? root.notes.trim() : "";
    if (rootNotes) {
      bodyLines.push(rootNotes, "");
    }

    // Recursively render children
    // depth 1 = ## (H2), since root is already # (H1) in the header
    function renderNode(node, depth) {
      const name = node.name || "";
      if (depth <= 5) {
        // H2 through H6
        bodyLines.push("#".repeat(depth + 1) + " " + name);
      } else {
        // Beyond H6: use nested list items
        const indent = "  ".repeat(depth - 6);
        bodyLines.push(indent + "- " + name);
      }
      bodyLines.push("");

      const notes = typeof node.notes === "string" ? node.notes.trim() : "";
      if (notes) {
        bodyLines.push(notes, "");
      }

      if (node.children) {
        for (const child of node.children) {
          renderNode(child, depth + 1);
        }
      }
    }

    if (root.children) {
      for (const child of root.children) {
        renderNode(child, 1);
      }
    }

    const markdown = headerLines.join("\n") + bodyLines.join("\n");
    const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8;" });
    await saveMapExport(blob, `${title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}_${dateStr}.md`, { data: root, docId: callbacks.getDocId?.(), allAttachments: root.id === state.data.id });
  } catch (e) {
    console.error("Failed to export markdown:", e);
    alert(t('vault.export.markdownFailed'));
  }
}

/**
 * Parse a markdown string into a tree of nodes.
 * Headings define structure: H1 = depth 1, H2 = depth 2, etc.
 * Text between headings becomes the preceding node's notes.
 * Text before the first heading becomes root notes.
 *
 * @param {string} text - Raw markdown text
 * @returns {{ rootName: string, rootNotes: string, children: Array<{name: string, notes: string, children: Array}> }}
 */
export function parseMarkdownToTree(text) {
  const lines = text.split('\n');
  const MAX_NODES = 5000;

  // Collect all headings with their line indices and levels,
  // skipping lines inside fenced code blocks (``` or ~~~)
  const headings = [];
  let inCodeBlock = false;
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trimStart();
    if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;

    const match = lines[i].match(/^(#{1,6})\s+(.*)/);
    if (match) {
      headings.push({ lineIndex: i, level: match[1].length, text: stripHtml(match[2].trim()) });
      if (headings.length > MAX_NODES) {
        throw new Error(t('vault.import.tooManyHeadings', { max: String(MAX_NODES) }));
      }
    }
  }

  // Text before first heading → root notes
  const firstHeadingLine = headings.length > 0 ? headings[0].lineIndex : lines.length;
  const rootNotes = sanitizeNotes(lines.slice(0, firstHeadingLine).join('\n').trim());

  // Build flat list of sections: { level, name, notes }
  const sections = [];
  for (let h = 0; h < headings.length; h++) {
    const heading = headings[h];
    const nextLine = h + 1 < headings.length ? headings[h + 1].lineIndex : lines.length;
    const noteLines = lines.slice(heading.lineIndex + 1, nextLine);
    while (noteLines.length > 0 && noteLines[0].trim() === '') noteLines.shift();
    const notes = sanitizeNotes(noteLines.join('\n').trimEnd());
    sections.push({ level: heading.level, name: heading.text, notes });
  }

  // Build tree from flat sections using a level stack
  const root = { children: [] };
  const stack = [{ level: 0, node: root }];

  for (const section of sections) {
    const node = { name: section.name, notes: section.notes || '', children: [] };

    while (stack.length > 1 && stack[stack.length - 1].level >= section.level) {
      stack.pop();
    }

    stack[stack.length - 1].node.children.push(node);
    stack.push({ level: section.level, node });
  }

  // If the first heading is H1, use it as the root name.
  // Its notes become rootNotes, and remaining nodes are children.
  let rootName = '';
  let finalRootNotes = rootNotes;
  let finalChildren = root.children;

  if (root.children.length > 0 && headings.length > 0 && headings[0].level === 1) {
    const firstNode = root.children[0];
    rootName = firstNode.name;
    // Combine any text before H1 with the H1's notes
    const notesParts = [rootNotes, firstNode.notes].filter(s => s).join('\n\n').trim();
    finalRootNotes = notesParts;
    // H1's children + remaining top-level nodes become the children
    finalChildren = [...firstNode.children, ...root.children.slice(1)];
  }

  return { rootName, rootNotes: finalRootNotes, children: finalChildren };
}

/**
 * Strip HTML tags from a string. Node names must be plain text.
 * @param {string} str
 * @returns {string}
 */
function stripHtml(str) {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '');
}

/**
 * Sanitize notes content. Removes dangerous HTML tags (script, iframe, etc.)
 * while preserving markdown formatting.
 * @param {string} str
 * @returns {string}
 */
function sanitizeNotes(str) {
  if (!str) return '';
  // Remove script/iframe/object/embed/form tags and their content
  return str
    .replace(/<\s*(script|iframe|object|embed|form|link|meta)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*(script|iframe|object|embed|form|link|meta)\b[^>]*\/?>/gi, '')
    // Remove event handler attributes from any remaining HTML
    .replace(/\bon\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '');
}

/**
 * Import a markdown file as a new map. Parses headings into a node tree,
 * creates a new map in the vault, and opens it.
 * @param {File} file - The .md file to import
 */
function importMarkdown(file) {
  const MAX_IMPORT_SIZE = 10 * 1024 * 1024; // 10MB
  if (file.size > MAX_IMPORT_SIZE) {
    alert(t('vault.import.markdownTooLarge'));
    return;
  }

  const progress = beginOperation(t('vault.import.markdownTitle'));
  const reader = new FileReader();
  reader.onprogress = event => progress.update({ label: t('vault.import.readingMarkdown'), current: event.loaded, total: event.lengthComputable ? event.total : undefined, unit: 'bytes' });
  reader.onerror = () => progress.fail(reader.error || new Error(t('vault.import.readFailed')));
  reader.onabort = () => progress.fail(new Error(t('vault.import.readInterrupted')));
  reader.onload = async (e) => {
    try {
      progress.update({ label: t('vault.import.processingMarkdown') });
      await progress.paint();
      const text = e.target.result;
      const { rootName, rootNotes, children } = parseMarkdownToTree(text);

      if (children.length === 0 && !rootNotes) {
        progress.fail(new Error(t('vault.import.noHeadings')));
        alert(t('vault.import.noHeadings'));
        return;
      }

      if (!callbacks.createMap || !callbacks.openDoc) {
        progress.fail(new Error(t('vault.import.noVault')));
        alert(t('vault.import.noVault'));
        return;
      }

      // Prompt for map name, defaulting to first H1 or filename
      const defaultName = rootName
        || file.name.replace(/\.(md|markdown|txt)$/i, '')
        || t('vault.import.defaultMarkdownName');
      const mapName = prompt(t('vault.import.mapNamePrompt'), defaultName);
      if (!mapName) { progress.finish(t('vault.import.cancelled')); return; }

      // Build root node with IDs and colors
      const rootId = crypto.randomUUID();
      const palette = getImportPalette();
      let colorIndex = 0;

      function assignColorsAndIds(node, parentColor) {
        node.id = crypto.randomUUID();
        if (!parentColor) {
          node.color = palette[colorIndex % palette.length];
          colorIndex++;
        } else {
          node.color = parentColor;
        }
        for (const child of node.children) {
          assignColorsAndIds(child, node.color);
        }
      }

      for (const child of children) {
        assignColorsAndIds(child, null);
      }

      const rootData = {
        id: rootId,
        name: mapName,
        notes: rootNotes,
        color: "#374151",
        children,
      };

      const initialState = {
        data: rootData,
        ganttScale: "auto",
        ganttZoom: 1,
        focusId: rootId,
        selectedId: rootId,
        view: "dashboard",
        userSetProjectDates: false,
        hideCompleted: false,
      };

      const folderId = callbacks.getCurrentFolderId?.() || null;
      const { docId } = await callbacks.createMap(mapName, folderId, initialState);
      await callbacks.openDoc(docId);
      progress.finish(t('vault.import.markdownDone'));
    } catch (err) {
      progress.fail(err);
      console.error("Failed to import markdown:", err);
      alert(t('vault.import.markdownFailed'));
    }
  };
  reader.readAsText(file);
}

/**
 * Get the color palette for import. Uses the current theme palette.
 * @returns {string[]}
 */
export function getImportPalette() {
  try {
    return getCurrentColors();
  } catch {
    return [
      "#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6",
      "#ec4899", "#06b6d4", "#f97316", "#14b8a6", "#6366f1",
    ];
  }
}
